import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { Certificate } from "src/app/models/Certificate";
import { SearchResult } from "src/app/models/SearchResult";
import { environment } from "src/environments/environment";

import ApiVersionConstants from "../constants/api-version-constants";
import LocalizationConstants from "../constants/localization-constants";
import { Document } from "../models/Document";
import { StatusTypes } from "../models/Enums";
import { ExcelExport } from "../models/ExcelExport";
import { GroupedStatus } from "../models/GroupedStatus";
import { Tool } from "../models/Tool";
import { ToolBase } from "../models/ToolBase";
import { ToolDetails } from "../models/ToolDetails";
import { ToolUpdate } from "./../models/ToolUpdate";
import { AuthService } from "./auth.service";

@Injectable({
  providedIn: "root",
})
export class ToolService {
  private baseUrl_V2 = `${environment.apiConfig.uri}/${ApiVersionConstants.V_2}/tool`;

  constructor(
    private http: HttpClient,
    private authService: AuthService,
  ) {}

  getByFilter(filter, aggregations = false) {
    return this.http.post<SearchResult<Tool>>(
      `${this.baseUrl_V2}/search?aggregations=${aggregations}`,
      filter,
    );
  }

  getToolDetails(companyId: string, serialNumber: string): Observable<ToolDetails> {
    return this.http.get<ToolDetails>(
      `${this.baseUrl_V2}/details?companyId=${companyId}&serialNumber=${serialNumber}`,
    );
  }

  getLastQuote(tool: Tool): Observable<Document> {
    return this.http.get<Document>(
      this.baseUrl_V2 +
        "/quoteDocument?companyId=" +
        tool.companyId +
        "&serialNumber=" +
        tool.serialNumber,
    );
  }

  getLastCertificate(tool: ToolBase): Observable<Certificate> {
    return this.http.get<Certificate>(
      this.baseUrl_V2 +
        "/certificate?companyId=" +
        tool.companyId +
        "&serialNumber=" +
        tool.serialNumber,
    );
  }

  canAddToRA(tool) {
    return (
      tool.eventStatus === StatusTypes.AtCustomer && this.authService.user?.allowServiceRequest
    );
  }

  canAddToPO(tool) {
    return tool.eventStatus === StatusTypes.AwaitingPO && this.authService.user?.allowPurchaseOrder;
  }

  updateLocation(companyId, serialNumber, location: ToolUpdate) {
    return this.http.put(
      this.baseUrl_V2 + "/location?companyId=" + companyId + "&serialNumber=" + serialNumber,
      location,
    );
  }

  updateDepartment(companyId, serialNumber, department: ToolUpdate) {
    return this.http.put(
      this.baseUrl_V2 + "/department?companyId=" + companyId + "&serialNumber=" + serialNumber,
      department,
    );
  }

  updateCalibrationSchedule(companyId, serialNumber, calibration: ToolUpdate) {
    return this.http.put(
      this.baseUrl_V2 +
        "/calibrationSchedule?companyId=" +
        companyId +
        "&serialNumber=" +
        serialNumber,
      calibration,
    );
  }

  updateServiceSchedule(companyId, serialNumber, service: ToolUpdate) {
    return this.http.put(
      this.baseUrl_V2 + "/serviceSchedule?companyId=" + companyId + "&serialNumber=" + serialNumber,
      service,
    );
  }

  updateActive(companyId: string, serialNumber: string, isActive: ToolUpdate) {
    const url = `${this.baseUrl_V2}/toggleActive?companyId=${companyId}&serialNumber=${serialNumber}`;
    return this.http.put(url, isActive);
  }

  updateNotes(companyId, serialNumber, notes: ToolUpdate) {
    return this.http.put(
      this.baseUrl_V2 + "/notes?companyId=" + companyId + "&serialNumber=" + serialNumber,
      notes,
    );
  }

  exportToExcel(toolExport: ExcelExport): Observable<Blob> {
    const url = `${this.baseUrl_V2}/download/xls`;
    return this.http.post(url, toolExport, { responseType: "blob" });
  }

  public hasActiveWorkOrders(companyId: string, serialNumber: string): Observable<boolean> {
    const url = `${this.baseUrl_V2}/hasActiveWorkOrders?companyId=${companyId}&serialNumber=${serialNumber}`;
    return this.http.get<boolean>(url);
  }

  statuses: GroupedStatus[] = [
    {
      key: 1,
      value: StatusTypes.AtCustomer,
      title: LocalizationConstants.WORKFLOW_STATUS_AVAILABLE,
      label: "Available",
    },
    {
      key: 2,
      value: StatusTypes.Requested,
      title: LocalizationConstants.WORKFLOW_STATUS_REQUESTED,
      label: "Service requested",
    },
    {
      key: 3,
      value: StatusTypes.Received,
      title: LocalizationConstants.WORKFLOW_STATUS_RECEIVED,
      label: "Received (awaiting quote)",
    },
    {
      key: 4,
      value: StatusTypes.AwaitingPO,
      title: LocalizationConstants.WORKFLOW_STATUS_AWAIT_PO,
      label: "Awaiting purchase order",
    },
    {
      key: 5,
      value: StatusTypes.InProcess,
      title: LocalizationConstants.WORKFLOW_STATUS_IN_PROCESS,
      label: "Repair in progress",
    },
    {
      key: 6,
      value: StatusTypes.Returning,
      title: LocalizationConstants.WORKFLOW_STATUS_RETURNING,
      label: "Returning",
    },
  ];

  worktypes = [
    { value: "common.worktype.contract", label: "Contract" },
    { value: "common.worktype.fixedPriceRepair", label: "Fixed Price Repair" },
    { value: "common.worktype.goodwill", label: "Goodwill" },
    { value: "common.worktype.internalServices", label: "Internal Services" },
    { value: "common.worktype.projects", label: "Projects" },
    {
      value: "common.worktype.resourceRelatedBilling",
      label: "Resource Related Billing",
    },
    { value: "common.worktype.warranty", label: "Warranty" },
  ];
}
